import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { ColorTarget, EMPTY_ENCODING, EncField, Encoding } from '@mode-vegas/vegas';
import { ButtonComponent, IconComponent } from '@mode/capra';
import { ChartFacade } from '@mode/charts/contract';
import {
  DEFAULT_UNASSIGNED_COLOR,
  FieldFormatHelpers,
  RenderDataModel,
  addFieldColor,
  generateSequentialColorRange,
  resolveFieldColor,
} from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { DialogHeaderComponent, LoaderComponent } from '@mode/shared/ui';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledField } from '@mode/shared/util-switch';
import { GradientColorEntries } from 'libs/shared/contract-common/src/lib/charts';
import { isEqual } from 'lodash';
import { BehaviorSubject, EMPTY, Observable, Subject, debounce, first, takeUntil, timer } from 'rxjs';
import { BackgroundSettingsToggleComponent } from '../background-settings-toggle/background-settings-toggle.component';
import { ColorValuesComponent, ColorValuesListItem } from '../color-values/color-values.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { FieldAndPaletteSelectorComponent } from '../field-and-palette-selector/field-and-palette-selector.component';
import { RangeInputsComponent } from '../range-inputs/range-inputs.component';
import {
  GradientValueLabels,
  SequentialDivergentColorPickerComponent,
} from '../sequential-divergent-color-picker/sequential-divergent-color-picker.component';
import { UnassignedColorPickerComponent } from '../unassigned-color-picker/unassigned-color-picker.component';
import * as ColorModalHelpers from './colors-modal-helpers';
import { ColorsModalData } from '@mode/charts/contract';

/** Maximum number of colors that we will auto-assign from the palette */
const MAX_ASSIGNED_COLORS = 250;

export interface GradientRangeValues {
  min: number | undefined;
  mid: number | undefined;
  max: number | undefined;
}

export interface ModalData extends ColorsModalData {
  currentField: CompiledField;
  allFields: CompiledField[];
  canUseAsBackground: boolean;
  isUseAsBackground: boolean;
  isText: boolean;
  isFieldLevel: boolean;
  domainLoader: DomainLoader;
}

export interface DomainLoader {
  fetchDomain(
    chartFacade: ChartFacade,
    currentField: CompiledField,
    onResult: (result: any[]) => void,
    onError: (error: string) => void
  ): void;
}

@Component({
  standalone: true,
  imports: [
    BackgroundSettingsToggleComponent,
    ButtonComponent,
    ColorValuesComponent,
    CommonModule,
    ConfirmationDialogComponent,
    DialogHeaderComponent,
    DragDropModule,
    FieldAndPaletteSelectorComponent,
    IconComponent,
    LoaderComponent,
    RangeInputsComponent,
    SequentialDivergentColorPickerComponent,
    UnassignedColorPickerComponent,
  ],
  selector: 'mode-colors-modal',
  templateUrl: './colors-modal.component.html',
  styleUrls: ['./colors-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorsModalComponent implements OnInit, OnDestroy {
  readonly applyColors: (colorField: EncField, fieldColor: ChartTypes.FieldColor, colorTarget: ColorTarget) => void;
  readonly onCancel: () => void;

  // Modal data
  orgPalettes: ReadonlyArray<Readonly<OrgTypes.ColorPalette>>;
  palettes: OrgTypes.ColorPalette[];
  currentPaletteToken: string;
  formatDefaultColor: string | undefined;
  queryRunToken: string | undefined;
  vizToken: string;
  view: FlamingoTypes.ModeView;
  encoding: Encoding | undefined;
  renderModel: RenderDataModel | undefined;
  fieldColors: ChartTypes.FieldColorMap;
  fieldFormats: ChartTypes.FieldFormatMap | undefined;
  chartHasSplitLegend = false;
  isManualUpdateMode = false;
  isChildModal = false;
  hideRangeControls = false;
  hideSteps = false;
  hideStepLabels = false;
  canUseAsBackground = false;
  isUseAsBackground = false;
  isText = false;
  isFieldLevel = false;
  currentField: CompiledField;
  domainLoader: DomainLoader;

  allColorFieldsInEncoding: CompiledField[] = [];

  savedFieldColor: ChartTypes.FieldColor | undefined;
  currentFieldColor: ChartTypes.FieldColor | undefined;
  measureValuesFieldColor: ChartTypes.FieldColor | undefined;

  savedColorTarget: ColorTarget;
  currentColorTarget: ColorTarget;

  selectedColorPalette: OrgTypes.ColorPalette | undefined;
  baseColorPalette: OrgTypes.ColorPalette | undefined;
  modifiedPalettes: OrgTypes.ColorPalette[] = [];
  continuousPalettes: OrgTypes.ColorPalette[] = [];
  discretePalettes: OrgTypes.ColorPalette[] = [];

  colorDomain: any[] = [];
  formattedColorDomain: any[] | undefined = [];

  gradientValueLabels: GradientValueLabels | undefined;
  gradientType: ChartTypes.GradientType | undefined;
  userGradientValues: GradientRangeValues | undefined;
  listOfItems: Array<ColorValuesListItem> = [];

  shouldShowConfirmationDialog = false;
  dialogField: CompiledField | null = null;

  isContinuousField = false;
  isDiscreteField = false;
  hasChanges = false;
  numberOfSteps = 0;

  fakeEncoding = EMPTY_ENCODING;

  // Observables
  destroyed$ = new Subject<void>();
  domainResult$ = new BehaviorSubject<any[] | undefined>(undefined);
  isLoading$ = new BehaviorSubject<boolean>(true);
  showLoader$: Observable<boolean>;

  // Error states
  hasLoadError = false;
  hasInputError = false;
  errorDescription = '';
  hasHighCardinalityWithUnassigned = false;

  unassignedColor = DEFAULT_UNASSIGNED_COLOR;

  constructor(
    private analyticsService: AnalyticsService,
    private chartFacade: ChartFacade,
    private dialogRef: MatLegacyDialogRef<ColorsModalComponent>,
    @Inject(MAT_LEGACY_DIALOG_DATA) private data: ModalData
  ) {
    this.applyColors = data.applyColors;
    this.onCancel = data.onCancel;

    this.orgPalettes = data.palettes || [];
    this.palettes = [...data.palettes] || [];
    this.currentPaletteToken = data.currentPaletteToken;
    this.formatDefaultColor = data.formatDefaultColor;
    this.queryRunToken = data.queryRunToken;
    this.vizToken = data.vizToken;
    this.view = data.view;
    this.encoding = data.encoding;
    this.fieldColors = { ...data.fieldColors } ?? {};
    this.renderModel = data.renderModel;
    this.chartHasSplitLegend = data.chartHasSplitLegend;
    this.isManualUpdateMode = data.isManualUpdateMode;
    this.fieldFormats = data.fieldFormats;
    this.savedColorTarget = data.savedColorTarget;
    this.currentColorTarget = data.savedColorTarget;
    this.isChildModal = data.isChildModal ?? false;
    this.hideRangeControls = data.hideRangeControls ?? false;
    this.hideSteps = data.hideSteps ?? false;
    this.hideStepLabels = data.hideStepLabels ?? false;
    this.isText = data.isText;
    this.canUseAsBackground = data.canUseAsBackground;
    this.isUseAsBackground = data.isUseAsBackground;
    this.isFieldLevel = data.isFieldLevel;

    this.currentField = data.currentField;
    this.domainLoader = data.domainLoader;

    this.allColorFieldsInEncoding = data.allFields;

    // if current field is `Measure Values` and chart has multiple color fields (is split legend),
    // set current field to first field in encoding
    if (this.currentField.isValues && this.allColorFieldsInEncoding.length) {
      // Save the field color associated with Measure Values. This will act
      // as a fallback if the individual fields don't have a field color.
      this.measureValuesFieldColor = resolveFieldColor(this.currentField, this.fieldColors);

      this.currentField = this.allColorFieldsInEncoding[0];
    }

    this.isContinuousField = this.currentField.isContinuous;
    this.isDiscreteField = this.currentField.isDiscrete;

    const modalTypeMessage = `Fields: ${this.allColorFieldsInEncoding.length}${
      this.isManualUpdateMode ? ', Manual update' : ''
    }`;
    this.analyticsService.track(AnalyticsEvent.Name.ModalShown, {
      location: AnalyticsEvent.SubContext.EditColorsModal,
      modal_type: modalTypeMessage,
      context: AnalyticsEvent.Context.Visualization,
      context_id: this.vizToken,
    });

    // Delay displaying the loader for 350ms to prevent a quick flash of the loader
    // when the data comes back quickly.
    this.showLoader$ = this.isLoading$.pipe(
      debounce((showLoader) => {
        return showLoader ? timer(350) : EMPTY;
      })
    );

    // Async handle color domain results
    this.domainResult$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (colorDomain) => {
        if (colorDomain === undefined) {
          return;
        }
        this.colorDomain = colorDomain;
        // Turn off the loader
        this.isLoading$.next(false);
        // Check for the persisted colors and use those colors and palette if found
        this.savedFieldColor = this.getSavedFieldColor();
        if (this.savedFieldColor === undefined) {
          this.savedFieldColor = this.buildDefaultFieldColor();
        }
        this.currentFieldColor = this.savedFieldColor;
        this.unassignedColor = this.currentFieldColor.unassignedColor ?? DEFAULT_UNASSIGNED_COLOR;
        this.numberOfSteps = this.currentFieldColor?.gradient?.numSteps ?? 0;
        // Set up the palettes
        this.setUpPalettes();

        if (this.isContinuousField) {
          this.setRangeValues();
        } else {
          this.applyColorMapToItems();
        }
        this.checkForChanges();

        const statusMsg = this.isContinuousField ? 'continuous' : `discrete: ${this.colorDomain.length}`;

        this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
          location: AnalyticsEvent.SubContext.EditColorsModal,
          action: 'Set color field',
          context: AnalyticsEvent.Context.Visualization,
          context_id: this.vizToken,
          status: statusMsg,
        });
      },
      error: (err) => {
        this.hasLoadError = true;
        this.errorDescription = err;
        // Turn off the loader
        this.isLoading$.next(false);
      },
    });
  }

  ngOnInit(): void {
    // Go get the color domain
    timer(50)
      .pipe(first())
      .subscribe(() => {
        this.fetchColorDomain();
      });
  }

  isDivergentPalette() {
    return this.selectedColorPalette?.type === OrgTypes.ColorPaletteType.Divergent;
  }

  ngOnDestroy(): void {
    this.domainResult$.complete();
    this.isLoading$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  fetchColorDomain() {
    this.hasLoadError = false;
    this.isLoading$.next(true);

    this.domainLoader.fetchDomain(
      this.chartFacade,
      this.currentField,
      (result) => this.domainResult$.next(result),
      (error) => this.domainResult$.error(error)
    );
  }

  setDivergentValues() {
    const modeField = this.renderModel?.makeField(this.colorField);
    const format = FieldFormatHelpers.resolvePaneTextFormatForField(this.fieldFormats, modeField);

    const domain = this.colorDomain as number[];
    this.userGradientValues = ColorModalHelpers.getCurrentGradientRange(this.currentFieldColor?.gradient);

    const min = this.userGradientValues.min ?? domain[0];
    const max = this.userGradientValues.max ?? domain[1];
    const mid = this.userGradientValues.mid ?? (min + max) / 2;

    this.gradientValueLabels = {
      min: {
        label: FieldFormatHelpers.formatValue(min, this.currentField, format),
        isAuto: this.userGradientValues.min == null,
      },
      mid: {
        label: FieldFormatHelpers.formatValue(mid, this.currentField, format),
        isAuto: this.userGradientValues.mid == null,
      },
      max: {
        label: FieldFormatHelpers.formatValue(max, this.currentField, format),
        isAuto: this.userGradientValues.max == null,
      },
    };
  }

  setSequentialValues() {
    const domain = this.colorDomain as number[];
    const format = this.getFieldFormat();

    this.userGradientValues = ColorModalHelpers.getCurrentGradientRange(this.currentFieldColor?.gradient);

    const min = this.userGradientValues.min ?? domain[0];
    const max = this.userGradientValues.max ?? domain[1];

    this.gradientValueLabels = {
      min: {
        label: FieldFormatHelpers.formatValue(min, this.currentField, format),
        isAuto: this.userGradientValues.min == null,
      },
      mid: undefined,
      max: {
        label: FieldFormatHelpers.formatValue(max, this.currentField, format),
        isAuto: this.userGradientValues.max == null,
      },
    };
  }

  setRangeValues() {
    if (this.isDivergentPalette()) {
      this.setDivergentValues();
    } else {
      this.setSequentialValues();
    }
    this.gradientType = this.currentFieldColor?.gradient?.gradientType;
  }

  cancel() {
    // Call the cancel callback
    this.onCancel();
    this.close();
  }

  reset() {
    this.currentFieldColor = this.savedFieldColor ?? this.buildDefaultFieldColor();
    this.currentColorTarget = this.savedColorTarget;
    this.isUseAsBackground = this.currentColorTarget === 'background';
    this.unassignedColor = this.currentFieldColor.unassignedColor ?? DEFAULT_UNASSIGNED_COLOR;
    this.numberOfSteps = this.currentFieldColor.gradient?.numSteps ?? 0;
    // Clear all modifications
    this.palettes = this.palettes.filter((palette) => !this.modifiedPalettes.includes(palette));
    this.modifiedPalettes = [];
    // Reinitialize the palettes based on the current field color
    this.setUpPalettes();

    // Reset all of the items
    if (this.isContinuousField) {
      this.setRangeValues();
    } else {
      this.applyColorMapToItems();
    }
    // Set the button state
    this.checkForChanges();
  }

  apply(close: boolean) {
    if (this.currentFieldColor == null) {
      return;
    }
    // Make sure the field is ready to be saved
    this.prepareFieldColorForSave();
    // Set the saveFieldColor to the current one
    this.savedFieldColor = this.currentFieldColor;
    // Set the savedColorTarget to the current one
    this.savedColorTarget = this.currentColorTarget;
    // Update the fieldColors so that it has the right values if the user changes fields.
    this.fieldColors = addFieldColor(this.colorField, this.currentFieldColor, this.fieldColors);
    this.applyColors(this.colorField, this.currentFieldColor, this.currentColorTarget);
    this.checkForChanges();
    if (close) {
      this.close();
    }
  }

  close() {
    this.dialogRef.close();
  }

  onConfirmDialogActionClick(field: CompiledField, discardChanges = true) {
    this.hasChanges = false;
    this.shouldShowConfirmationDialog = false;

    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.EditColorsModal,
      action: discardChanges ? 'Discard unapplied changes' : 'Apply changes',
      context: AnalyticsEvent.Context.Visualization,
      context_id: this.vizToken,
    });

    if (discardChanges) {
      this.onFieldChange(field);
    } else {
      this.apply(false);
      this.onFieldChange(field);
    }
  }

  onFieldChange(field: CompiledField) {
    if (this.hasChanges) {
      // if there are un-applied changes, display confirmation dialog prompt
      this.shouldShowConfirmationDialog = true;
      this.dialogField = field;
      return;
    }

    this.currentField = field;
    this.isContinuousField = this.currentField.isContinuous;
    this.isDiscreteField = this.currentField.isDiscrete;
    this.hasHighCardinalityWithUnassigned = false;

    // Fetch the color domain for this field
    this.fetchColorDomain();
  }

  onPaletteChange(palette: OrgTypes.ColorPalette) {
    this.selectedColorPalette = palette;
    const colors = this.selectedColorPalette?.value.split(',').map((color) => color.trim());
    if (this.isContinuousField) {
      this.setContinuousFieldColor(colors);
    } else {
      this.setDiscreteFieldColor(colors, this.buildColorMap(colors));
    }
    // Reinitialize the palettes based on the current field color
    this.setUpPalettes();

    if (this.isDiscreteField) {
      // Rebuild the item list based on the new palette colors
      this.applyColorMapToItems();
    } else if (this.isContinuousField) {
      this.setRangeValues();
    }

    this.checkForChanges();
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.EditColorsModal,
      action: 'Changed palette',
      context: AnalyticsEvent.Context.Visualization,
      context_id: this.vizToken,
    });
  }

  onStepChange(colors: string[], numberOfSteps: number) {
    this.numberOfSteps = numberOfSteps;
    // Update currentFieldColor with values from numberOfSteps
    this.setContinuousFieldColor(colors);
    // Update the button state
    this.checkForChanges();
  }

  onContinuousColorChange(colors: string[]) {
    // Build the persistable field color object
    this.setContinuousFieldColor(colors);
    // We probably now have a different set of colors so we have a modified palette.
    this.setUpPalettes();
    // Update the button state
    this.checkForChanges();
  }

  onDiscreteColorChange(colorItem: ColorValuesListItem) {
    const colorMap = { ...(this.currentFieldColor?.map ?? {}) };

    const paletteColors = [...(this.currentFieldColor?.palette ?? [])];
    if (!paletteColors.includes(colorItem.colorHexCode)) {
      paletteColors.push(colorItem.colorHexCode);
    }
    const index = paletteColors.indexOf(colorItem.colorHexCode);
    colorMap[colorItem.unformattedValue] = index;

    this.setDiscreteFieldColor(paletteColors, colorMap);
    this.setUpPalettes();
    this.checkForChanges();
  }

  onMarkTargetClick(target: ColorTarget) {
    this.currentColorTarget = target;
    this.isUseAsBackground = target === 'background';
    this.checkForChanges();
  }

  onBulkUnassign(items: ColorValuesListItem[]) {
    if (this.currentFieldColor == null) {
      return;
    }
    // Unassigned values get mapped to -1. On save we need to
    // trim the map to only include up to 250 unmapped values.
    const colorMap = { ...(this.currentFieldColor.map ?? {}) };
    items.forEach((item) => {
      colorMap[item.unformattedValue] = -1;
    });
    this.currentFieldColor = {
      ...this.currentFieldColor,
      unassignedColor: this.unassignedColor ?? DEFAULT_UNASSIGNED_COLOR,
    };
    this.setDiscreteFieldColor([...this.currentFieldColor.palette], colorMap);

    this.applyColorMapToItems();
    this.setUpPalettes();
    this.checkForChanges();

    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.EditColorsModal,
      action: `Unassign colors (${items.length} of ${Object.keys(this.currentFieldColor.map ?? {}).length})`,
      context: AnalyticsEvent.Context.Visualization,
      context_id: this.vizToken,
    });
  }

  onUnassignedColorSelected(color: string) {
    this.unassignedColor = color;
    // Update the field color
    if (this.currentFieldColor != null) {
      this.currentFieldColor = {
        ...this.currentFieldColor,
        unassignedColor: this.unassignedColor,
      };
    }
    const colorItems = this.listOfItems.map((item) => {
      if (!item.isAssigned) {
        item.colorHexCode = this.unassignedColor;
      }
      return item;
    });
    this.listOfItems = colorItems;

    this.checkForChanges();
  }

  onRangeIsValidChange(isValid: boolean) {
    this.hasInputError = !isValid;
  }

  onContinuousRangeUpdated(range: GradientRangeValues) {
    // Use the current colors with the new range
    const colors = [...(this.currentFieldColor?.palette ?? [])];
    const continuousGradient = ColorModalHelpers.makeContinuousGradient(
      colors.length === 2 ? ChartTypes.GradientType.SEQUENTIAL : ChartTypes.GradientType.DIVERGING,
      colors,
      range,
      this.numberOfSteps > 0,
      this.numberOfSteps
    );

    // Update the gradient ranges in the current field color.
    this.currentFieldColor = {
      colorPaletteToken: this.selectedColorPalette?.token ?? 'unknown',
      palette: [...colors],
      map: {},
      gradient: continuousGradient,
    };

    this.setRangeValues();
    this.checkForChanges();
  }

  setUpPalettes() {
    if (this.currentFieldColor == null) {
      return;
    }
    const currentPaletteToken = this.currentFieldColor.colorPaletteToken;
    const orgPalette = ColorModalHelpers.getDefaultPalette(this.currentField, this.orgPalettes, currentPaletteToken);

    const savedColorValues = this.currentFieldColor.palette.map((color) => color.trim()).join(',');
    const expectedPaletteValues = orgPalette?.value
      .split(',')
      .map((color) => color.trim())
      .join(',');

    if (orgPalette !== undefined && savedColorValues.toLowerCase() !== expectedPaletteValues?.toLowerCase()) {
      // This palette has colors which have been modified from the original.
      // This will be displayed as a modified palette in the palette dropdown.
      // If we have already created a modified palette, then we need to remove it
      // and add a new one.
      // Otherwise we can just add a new one.
      const modifiedPalette = this.modifiedPalettes.find((p) => p.token === currentPaletteToken);
      let updatedPalettes = [...this.palettes];
      if (modifiedPalette) {
        // Remove it from the palettes
        updatedPalettes = updatedPalettes.filter((palette) => palette !== modifiedPalette);
        // Remove it from the modified palettes.
        this.modifiedPalettes = this.modifiedPalettes.filter((palette) => palette !== modifiedPalette);
      }

      const currentPaletteType = (() => {
        if (this.currentFieldColor.gradient?.gradientType === undefined) {
          return orgPalette.type;
        }
        switch (this.currentFieldColor.gradient?.gradientType) {
          case ChartTypes.GradientType.SEQUENTIAL:
            return OrgTypes.ColorPaletteType.Sequential;
          case ChartTypes.GradientType.DIVERGING:
            return OrgTypes.ColorPaletteType.Divergent;
          default:
            return orgPalette.type;
        }
      })();

      const newModifiedPalette = {
        ...orgPalette,
        name: `${orgPalette.name} (Modified)`,
        value: savedColorValues,
        type: currentPaletteType,
      };
      updatedPalettes.push(newModifiedPalette);
      this.palettes = updatedPalettes;
      this.modifiedPalettes.push(newModifiedPalette);
      this.selectedColorPalette = newModifiedPalette;
    } else {
      // The palette is unmodified from the original.
      this.selectedColorPalette = this.palettes.find((palette) => palette.token === currentPaletteToken);
    }
    this.baseColorPalette = this.orgPalettes.find((p) => p.token === this.selectedColorPalette?.token);

    this.partitionPalettes();
  }

  partitionPalettes() {
    this.palettes.sort((a, b) => a.name.localeCompare(b.name));
    this.continuousPalettes = this.palettes.filter(
      (palette: OrgTypes.ColorPalette) =>
        palette.type === OrgTypes.ColorPaletteType.Sequential || palette.type === OrgTypes.ColorPaletteType.Divergent
    );

    this.discretePalettes = this.palettes.filter(
      (palette: OrgTypes.ColorPalette) => palette.type === OrgTypes.ColorPaletteType.Categorical
    );
  }

  setContinuousFieldColor(colors: string[]) {
    // Get the current range settings. These will be reused with the new colors
    const gradientRange = ColorModalHelpers.getCurrentGradientRange(this.currentFieldColor?.gradient);
    // Build the gradient entries
    const continuousGradient = ColorModalHelpers.makeContinuousGradient(
      colors.length === 2 ? ChartTypes.GradientType.SEQUENTIAL : ChartTypes.GradientType.DIVERGING,
      colors,
      gradientRange,
      this.numberOfSteps > 0,
      this.numberOfSteps
    );

    this.currentFieldColor = {
      colorPaletteToken: this.selectedColorPalette?.token ?? 'unknown',
      palette: [...colors],
      map: {},
      gradient: continuousGradient,
    };
  }

  setDiscreteFieldColor(colors: string[], colorMap: ChartTypes.ColorEntries) {
    this.currentFieldColor = {
      colorPaletteToken: this.selectedColorPalette?.token ?? 'unknown',
      palette: [...colors],
      map: colorMap,
      unassignedColor: this.currentFieldColor?.unassignedColor,
    };
  }

  buildDefaultFieldColor() {
    // We want to match the new fieldColor to the existing colors in use.
    // Previous, we resolved the base color from:
    //  1. The color stored in the Mark properties
    //  2. The first color from the default palette.
    const markColor =
      ColorModalHelpers.getMarkColorValue(this.encoding ?? EMPTY_ENCODING, this.colorField) ?? this.formatDefaultColor;

    const defaultPalette = ColorModalHelpers.getDefaultPalette(
      this.currentField,
      this.orgPalettes,
      this.currentPaletteToken
    );

    if (defaultPalette === undefined) {
      throw Error('Unable to locate palette');
    }

    let continuousGradient: ChartTypes.ContinuousGradient | undefined;
    let map: ChartTypes.ColorEntries = {};
    let paletteColors = [];
    if (this.isContinuousField) {
      // If this field is in Measure Values, then we will fall back to the
      // field colors on Measure Values if it is set.
      const isInMeasureValues = this.encoding?.value.some((f) => f.id === this.currentField.alias) ?? false;
      const gradient =
        this.measureValuesFieldColor !== undefined && isInMeasureValues
          ? this.measureValuesFieldColor.gradient
          : undefined;

      // By default, we will use a sequential palette unless we are using the gradient
      // from Measure Values
      const paletteType = gradient !== undefined ? gradient.gradientType : ChartTypes.GradientType.SEQUENTIAL;

      const defaultPaletteColors = defaultPalette.value
        .split(',')
        .map((color) => color.trim())
        .slice(0, paletteType === ChartTypes.GradientType.DIVERGING ? 3 : 2);

      const range = isInMeasureValues ? ColorModalHelpers.getCurrentGradientRange(gradient) : undefined;
      const stepped = isInMeasureValues ? gradient?.stepped : false;
      const numSteps = isInMeasureValues ? gradient?.numSteps : undefined;

      if (isInMeasureValues && this.measureValuesFieldColor) {
        // If Measure Values has a color defined, use the palette from there.
        paletteColors = this.measureValuesFieldColor ? [...this.measureValuesFieldColor.palette] : defaultPaletteColors;
      } else {
        // Generate a set of palette colors.
        //  - if there is a single mark color, use that to generate a gradient.
        //  - otherwise, just grab the first colors from the default palette.
        paletteColors =
          markColor !== undefined
            ? generateSequentialColorRange(markColor, gradient).map((color) => color.formatHex())
            : defaultPaletteColors;
      }

      continuousGradient = ColorModalHelpers.makeContinuousGradient(
        paletteType,
        paletteColors,
        range,
        stepped,
        numSteps
      );
    } else {
      paletteColors = defaultPalette.value.split(',').map((color) => color.trim());
      map = this.buildColorMap(paletteColors);
    }
    this.selectedColorPalette = defaultPalette;
    return {
      colorPaletteToken: defaultPalette.token,
      palette: paletteColors.map((color) => color.toUpperCase()),
      map,
      gradient: continuousGradient,
    };
  }

  buildColorMap(paletteColors: string[]) {
    const colorMap: ChartTypes.ColorEntries = {};
    // When building the default map, we only map the first MAX_ASSIGNED_COLORS values
    const paletteLen = paletteColors.length;
    this.colorDomain.forEach((domain, index) => {
      if (index >= MAX_ASSIGNED_COLORS) {
        colorMap[domain] = -1;
        return;
      }
      colorMap[domain] = index % paletteLen;
    });
    return colorMap;
  }

  applyColorMapToItems() {
    this.listOfItems = [];
    const colorMap = this.currentFieldColor?.map ?? {};
    const palette = this.currentFieldColor?.palette ?? [];

    // Determine the next colors to use for any unmapped value. This seems
    // very expensive for large color maps. Using this for now because
    // it mimicks what is done in 'chart-color.ts' and we need these
    // default mappings to match between the modal and Muze config.
    let availableIndexes: number[] = Array.from(Array(palette.length).keys());
    const domainSet = new Set(this.colorDomain.map((v) => v?.toString() ?? 'null'));
    let mappedCount = 0;
    Object.entries(colorMap).forEach(([key, value]) => {
      const index = availableIndexes.indexOf(value);
      index !== -1 && availableIndexes.splice(index, 1);
      if (domainSet.has(key)) {
        mappedCount += 1;
      }
    });

    const getNextIndex = (): number => {
      if (++mappedCount >= MAX_ASSIGNED_COLORS) {
        return -1;
      }
      if (availableIndexes.length === 0) {
        availableIndexes = Array.from(Array(palette.length).keys());
      }
      return availableIndexes.shift() || 0;
    };

    const format = this.getFieldFormat();
    let hasUnassigned = false;

    this.colorDomain.forEach((value) => {
      const isAssigned = Object.prototype.hasOwnProperty.call(colorMap, value);
      const colorIndex = isAssigned ? colorMap[value] : getNextIndex();
      if (colorIndex < 0) {
        hasUnassigned = true;
      }

      const color = colorIndex >= 0 ? palette[colorIndex] : this.unassignedColor;
      const formattedValue = FieldFormatHelpers.formatValue(value, this.currentField, format) ?? 'Null';
      this.listOfItems.push({
        formattedValue: String(formattedValue),
        unformattedValue: value,
        colorHexCode: color,
        isAssigned: colorIndex >= 0,
      });
    });
    this.hasHighCardinalityWithUnassigned = mappedCount >= MAX_ASSIGNED_COLORS && hasUnassigned;
  }

  prepareFieldColorForSave() {
    if (this.currentFieldColor == null) {
      return;
    }
    if (this.isDiscreteField) {
      // For discrete fields we need to clean up the unassigned values.
      // Due to backward compatibility, we need to only set the unassigned color when
      // there are unassigned values. The presence of the unassigned color
      // dictates whether or not an unassigned value gets the unassigned color or a
      // color from the palette.
      // Unassigned should only happen for high cardinality (>250 values) or when the user
      // explicitly unassigns one or more values.

      // Example:
      // The user explicitly maps all available month data to colors (Jan = 0, Feb = 1, Mar = 2).
      // On April 1st, there is a new month. By default, we want that to pick up the next
      // color from the palette and not use the unassigned color.
      // Conversely, the user explicitly maps Jan = unassigned, Feb = 0, Mar = 1, Apr = 2.
      // Since there is an explicit unassign Jan will get the unassigned color and so will
      // May, Jun, Jul, etc if they are added to the data.

      // Unassigned values have a palette index of -1. These count much like assigned
      // values when determining if we are at the high cardinality limit. The difference
      // is that we will remove unassigned values from the map to get below the limit, but
      // we never remove assigned values (even if we exceed the limit).

      // Make a Set from the map keys
      const mappedValues = new Set(Object.keys(this.currentFieldColor?.map ?? {}));

      // If any domain value isn't in the mapped, then we have unassigned values.
      const hasMissingDomainValues = this.colorDomain.some((value) => !mappedValues.has(value));
      // We can also have unassigned values if any of the values is -1.
      const hasUnassignedValues = Object.values(this.currentFieldColor.map).some((value) => value === -1);

      // Check if our map exceeds our soft limit. If so,  Remove any explicitly unassigned values
      // to reduce map size to as close to MAX_ASSIGNED_COLORS as possible.
      if (hasUnassignedValues && mappedValues.size > MAX_ASSIGNED_COLORS) {
        // Calculate how many values are assigned actual colors
        const assignedCount = Object.values(this.currentFieldColor.map).reduce(
          (count, value) => (value >= 0 ? count + 1 : count),
          0
        );
        // The difference between the MAX_ASSIGNED_COLORS and how many are assigned
        // actual colors is how many unassigned values we can have.
        let availableUnassignedCount = MAX_ASSIGNED_COLORS - assignedCount;
        const updatedMap = Object.entries(this.currentFieldColor.map).reduce(
          (entries, [key, value]) => {
            if (value >= 0) {
              entries[key] = value;
            } else if (availableUnassignedCount > 0) {
              entries[key] = value;
              availableUnassignedCount -= 1;
            }
            return entries;
          },
          {} as Record<string, number>
        );
        this.currentFieldColor = { ...this.currentFieldColor, map: updatedMap };
      }
      // Set the unassigned color if we have any unassigned domain values.
      if (hasMissingDomainValues || hasUnassignedValues) {
        this.currentFieldColor = { ...this.currentFieldColor, unassignedColor: this.unassignedColor };
      }
    } else if (this.isContinuousField && this.currentFieldColor.gradient != null) {
      // For a continuous field, we need to ensure that there are no gradient entries pointing to
      // undefined colors. This can happen when switching from a 3-color divergent to 2-color sequential.
      // We save the midpoint entry so that it comes back if you switch back to divergent. However,
      // we don't want to persist the empty color value.
      const nonEmptyEntries = Object.entries(this.currentFieldColor.gradient?.colorEntries ?? {}).reduce(
        (nonEmpty, [key, value]) => {
          if (value != null) {
            nonEmpty[key as unknown as ChartTypes.GradientStep | number] = value;
          }
          return nonEmpty;
        },
        {} as GradientColorEntries
      );
      this.currentFieldColor = {
        ...this.currentFieldColor,
        gradient: { ...this.currentFieldColor.gradient, colorEntries: nonEmptyEntries },
      };
    }
  }

  getSavedFieldColor() {
    const fieldColor = resolveFieldColor(this.currentField, this.fieldColors);

    // It is possible that the saved field color is the old style.
    // This was only used for setting the color of a field in Measure Values
    // This type of field color will have a sequential palette with
    // only the data_midpoint defined.
    if (
      fieldColor?.gradient?.gradientType === ChartTypes.GradientType.SEQUENTIAL &&
      Object.keys(fieldColor.gradient.colorEntries).length === 1 &&
      fieldColor.gradient.colorEntries.DATA_MAX === undefined &&
      fieldColor.gradient.colorEntries.DATA_MIN === undefined &&
      fieldColor.gradient.colorEntries.DATA_MIDPOINT !== undefined
    ) {
      // Generate a new gradient using the midpoint color
      const palette = generateSequentialColorRange(fieldColor.gradient.colorEntries.DATA_MIDPOINT).map((color) =>
        color.formatHex()
      );
      const continuousGradient = ColorModalHelpers.makeContinuousGradient(ChartTypes.GradientType.SEQUENTIAL, palette);
      const defaultPalette = ColorModalHelpers.getDefaultPalette(this.currentField, this.orgPalettes);
      return {
        ...fieldColor,
        colorPaletteToken: defaultPalette?.token ?? fieldColor.colorPaletteToken,
        palette,
        gradient: continuousGradient,
      };
    }
    return fieldColor;
  }

  checkForChanges() {
    this.hasChanges =
      !isEqual(this.savedFieldColor, this.currentFieldColor) ||
      !isEqual(this.savedColorTarget, this.currentColorTarget);
  }

  getFieldFormat() {
    const modeField = this.renderModel?.makeField(this.colorField);
    return FieldFormatHelpers.resolvePaneTextFormatForField(this.fieldFormats, modeField);
  }

  get colorField(): EncField {
    return { ...this.currentField.toEncField(), id: this.currentField.alias };
  }
}
